import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-3 text-[11px] text-[#a8862f] font-semibold tracking-[0.25em] uppercase mb-5">
    <span className="gold-rule" />
    <span className="text-gold-shine">{children}</span>
    <span className="gold-rule" />
  </div>
);

export const H1 = ({ children, gradient }) => (
  <h1 className="font-display font-tight text-5xl md:text-6xl lg:text-7xl text-[#0e2408] font-semibold leading-[1]">
    {children}
    {gradient && <span className="block text-gold-shine">{gradient}</span>}
  </h1>
);

export const H2 = ({ children, gradient }) => (
  <h2 className="font-display font-tight text-4xl md:text-5xl lg:text-6xl text-[#0e2408] font-semibold leading-[1.05]">
    {children}
    {gradient && <span className="block text-gold-shine">{gradient}</span>}
  </h2>
);

export const Lead = ({ children }) => <p className="text-[#4a5a44] text-lg md:text-xl leading-[1.5] max-w-3xl">{children}</p>;

export const CTAButton = ({ to, children, variant = "primary", href, ...rest }) => {
  const base = "group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium";
  const styles = variant === "primary"
    ? "btn-primary"
    : variant === "gold"
      ? "btn-gold"
      : "glass-frosted text-[#0e2408] hover:bg-white/85 border border-[#c9a34a]/30";
  const Comp = href ? "a" : Link;
  const props = href ? { href, ...rest } : { to };
  return (
    <Comp className={`${base} ${styles}`} {...props}>
      {children}
      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
    </Comp>
  );
};

export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`relative py-24 md:py-32 px-6 ${className}`}>{children}</section>
);

export const Container = ({ children, className = "" }) => (
  <div className={`relative max-w-6xl mx-auto ${className}`}>{children}</div>
);

export const GlassCard = ({ children, className = "", hover = true }) => (
  <motion.div whileHover={hover ? { y: -6, rotateX: 3, rotateY: -3 } : {}} transition={{ type: "spring", stiffness: 200, damping: 22 }} className={`preserve-3d glass-frosted rounded-3xl p-7 shadow-apple hover:border-[#c9a34a]/40 ${className}`}>
    {children}
  </motion.div>
);

export const Orbs = () => (
  <>
    <div className="orb orb-forest w-[500px] h-[500px] top-[10%] left-[-10%]" />
    <div className="orb orb-gold w-[500px] h-[500px] bottom-[5%] right-[-10%]" />
    <div className="orb orb-pale w-[400px] h-[400px] top-[45%] left-[45%]" />
  </>
);

export const PageHero = ({ eyebrow, title, gradient, lead, cta }) => (
  <section className="relative bg-hero-light overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24 px-6">
    <Orbs />
    <div className="relative max-w-6xl mx-auto text-center">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <H1 gradient={gradient}>{title}</H1>
      {lead && <div className="mt-6 flex justify-center"><Lead>{lead}</Lead></div>}
      {cta && <div className="mt-10 flex flex-wrap justify-center gap-3">{cta}</div>}
    </div>
  </section>
);

export const CompareTable = ({ rows }) => (
  <div className="glass-frosted rounded-3xl overflow-hidden shadow-apple border border-[#c9a34a]/25">
    <div className="grid grid-cols-3 bg-[#0e2408] text-white text-[13px] font-semibold tracking-wide px-5 py-4">
      <div>Feature</div><div>Masjid Suite</div><div>Traditional Software</div>
    </div>
    {rows.map(([f, ms, trad], i) => (
      <div key={i} className={`grid grid-cols-3 gap-3 px-5 py-4 text-[13px] ${i % 2 === 0 ? "bg-white/50" : "bg-[#f6e8c6]/25"} border-t border-[#c9a34a]/15`}>
        <div className="text-[#0e2408] font-medium">{f}</div>
        <div className="text-[#163410]">{ms}</div>
        <div className="text-[#6b7563]">{trad}</div>
      </div>
    ))}
  </div>
);
